import React, { useCallback, useEffect, useRef, useState } from 'react';
import { clsx } from 'clsx';
import {
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  BadgeCheck,
  Check,
  ClipboardCopy,
  Copy,
  Download,
  ExternalLink,
  Film,
  Hash,
  ImagePlus,
  Info,
  LayoutGrid,
  Loader2,
  MessageSquare,
  Plus,
  RotateCcw,
  Save,
  Send,
  Trash2,
  Undo2,
  Users,
  X,
} from 'lucide-react';
import { Modal, ConfirmDialog } from '@components/Dialog';
import { Button } from '@components/Button';
import { ApiErrorModal } from '@components/ApiErrorModal';
import { DateInputBR } from '@components/DateInputBR';
import { useProfile } from '@contexts/ProfileContext';
import { useActivityModal } from '@contexts/ActivityModalContext';
import { usePermissions } from '@hooks/usePermissions';
import socialService from '@services/socialService';
import mediaService from '@services/mediaService';
import type {
  AspectRatio,
  FormatRule,
  HashtagGroup,
  MediaBrief,
  Network,
  Pillar,
  PostFormat,
  SaveMediaInput,
  SavePostRequest,
  SaveTargetInput,
  SocialAccount,
  SocialPost,
} from '@/types/social';
import { COMMENT_KIND_LABELS, NETWORKS, NETWORK_LABELS, TARGET_STATUS_LABELS } from '@/types/social';
import { formatDateTimeBR } from '@/utils/formatters';
import {
  ASPECT_CLASS,
  appendHashtags,
  charCount,
  coverImageAllowed,
  effectiveCaption,
  emptySaveRequest,
  extractHashtags,
  findTargetIndex,
  formatCoverTime,
  MAX_COLLABORATORS,
  parseCollaborators,
  formatPostTime,
  formatsOf,
  joinDateTime,
  presetTargets,
  timeOf,
  toSaveRequest,
} from '@/utils/social';
import { useFormatRules } from '@hooks/useFormatRules';
import { BriefThumb, NetworkIcon, StatusChip } from './SocialBits';
import { MediaPickerDialog } from './MediaPickerDialog';
import { ReelCoverDialog } from './ReelCoverDialog';

export interface PostPreset {
  scheduled_at?: string | null;
  pillar_id?: number | null;
  strategy?: boolean;
  title?: string;
  media?: MediaBrief[];
  formats?: PostFormat[];
  /** Perfil escolhido na tela (Grade, Calendário…): o post novo já nasce com a variação dele. */
  account?: { id: number; network: Network };
}

interface PostComposerProps {
  postId?: number;
  preset?: PostPreset;
  onClose: () => void;
  /** savedId: id do post salvo (post novo passa a ser editado). */
  onChanged: (savedId?: number) => void;
}

const PRIVACY_LABELS: Record<string, string> = {
  PUBLIC_TO_EVERYONE: 'Público',
  MUTUAL_FOLLOW_FRIENDS: 'Amigos (seguem de volta)',
  FOLLOWER_OF_CREATOR: 'Seguidores',
  SELF_ONLY: 'Só eu (privado)',
  public: 'Público',
  unlisted: 'Não listado',
  private: 'Privado',
};

type Options = Record<string, boolean | string>;

const readOptions = (raw?: string | null): Options => {
  try {
    const v = raw ? JSON.parse(raw) : {};
    return v && typeof v === 'object' ? (v as Options) : {};
  } catch {
    return {};
  }
};

const mediaInput = (b: MediaBrief, ratio?: AspectRatio): SaveMediaInput => ({ media_id: b.id, aspect_ratio: ratio ?? null });

/** Contador "n / máx" vermelho quando passa. */
const Counter: React.FC<{ n: number; max: number; suffix?: string }> = ({ n, max, suffix }) => (
  <span className={clsx('text-[11px]', max > 0 && n > max ? 'text-red-600 font-semibold' : 'text-slate-400')}>
    {n.toLocaleString('pt-BR')}
    {max > 0 && ` / ${max.toLocaleString('pt-BR')}`}
    {suffix}
  </span>
);

/**
 * Compositor do post (modal global, ver SocialPostContext): post base + variações por rede,
 * mídias da Biblioteca com corte e ponto focal, prévia, problemas por rede, fluxo de aprovação
 * e conversa. Documentação de uso em src/docs/redes-sociais/posts.md.
 */
export const PostComposer: React.FC<PostComposerProps> = ({ postId, preset, onClose, onChanged }) => {
  const rules = useFormatRules();
  const { assignableMembers, self } = useProfile();
  const { openActivity } = useActivityModal();
  const { checkPermission } = usePermissions();
  const canWrite = checkPermission('social.write').hasPermission;
  const canPublishNow = checkPermission('social.publish_now').hasPermission;

  const [post, setPost] = useState<SocialPost | null>(null);
  const [form, setForm] = useState<SavePostRequest>(() =>
    emptySaveRequest({
      title: preset?.title ?? '',
      scheduled_at: preset?.scheduled_at ?? null,
      pillar_id: preset?.pillar_id ?? null,
      strategy: preset?.strategy ?? false,
      owner_member_id: self?.id ?? null,
      media: (preset?.media ?? []).map((m) => mediaInput(m)),
    }),
  );
  const [briefs, setBriefs] = useState<Record<number, MediaBrief>>(() =>
    Object.fromEntries((preset?.media ?? []).map((m) => [m.id, m])),
  );
  const [accounts, setAccounts] = useState<SocialAccount[]>([]);
  const [pillars, setPillars] = useState<Pillar[]>([]);
  const [groups, setGroups] = useState<HashtagGroup[]>([]);
  const [tab, setTab] = useState<number>(-1); // -1 = base; i = targets[i]
  const [loading, setLoading] = useState(!!postId);
  const [busy, setBusy] = useState<string | null>(null);
  const [dirty, setDirty] = useState(!postId);
  const [error, setError] = useState<unknown>(null);
  const [picker, setPicker] = useState<{ target: number; kinds?: ('image' | 'video')[]; max: number } | null>(null);
  const [decision, setDecision] = useState<null | 'submit' | 'approve' | 'request_changes' | 'withdraw' | 'publish'>(null);
  const [decisionText, setDecisionText] = useState('');
  const [permalinks, setPermalinks] = useState<Record<number, string>>({});
  const [comment, setComment] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [focusMedia, setFocusMedia] = useState<{ target: number; index: number } | null>(null);
  const [coverMedia, setCoverMedia] = useState<{ target: number; index: number } | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  // Post já carregado nesta instância (o 1º salvamento de um post novo não recarrega).
  const loadedId = useRef<number | null>(null);

  const applyPost = useCallback((p: SocialPost) => {
    loadedId.current = p.id;
    setPost(p);
    setForm(toSaveRequest(p));
    setBriefs((prev) => {
      const next = { ...prev };
      for (const m of p.media) {
        if (m.media) next[m.media_id] = m.media;
        if (m.cover && m.cover_media_id) next[m.cover_media_id] = m.cover;
      }
      return next;
    });
    setDirty(false);
  }, []);

  useEffect(() => {
    socialService.listAccounts().then(setAccounts).catch(() => undefined);
    socialService.listPillars().then(setPillars).catch(() => undefined);
    socialService.listHashtagGroups().then(setGroups).catch(() => undefined);
  }, []);

  useEffect(() => {
    if (!postId || loadedId.current === postId) return;
    setLoading(true);
    socialService
      .getPost(postId)
      .then(applyPost)
      .catch(setError)
      .finally(() => setLoading(false));
  }, [postId, applyPost]);

  // Formatos pedidos no preset (ex.: "Adaptar para…" a partir de uma mídia) ou o perfil da tela.
  useEffect(() => {
    // Bloco de estratégia não ganha variação só por causa do perfil.
    const account = preset?.strategy ? undefined : preset?.account;
    if (postId || (!preset?.formats?.length && !account) || rules.length === 0) return;
    setForm((f) => (f.targets.length > 0 ? f : { ...f, targets: presetTargets(rules, preset?.formats, account) }));
  }, [postId, preset, rules]);

  const update = (patch: Partial<SavePostRequest>) => {
    setForm((f) => ({ ...f, ...patch }));
    setDirty(true);
  };
  const updateTarget = (i: number, patch: Partial<SaveTargetInput>) => {
    setForm((f) => ({ ...f, targets: f.targets.map((t, j) => (j === i ? { ...t, ...patch } : t)) }));
    setDirty(true);
  };

  const ruleOf = useCallback((format: PostFormat): FormatRule | undefined => rules.find((r) => r.format === format), [rules]);
  const locked = post ? !post.can_edit : false;
  const readOnly = locked || !canWrite;

  const date = form.scheduled_at ? form.scheduled_at.slice(0, 10) : '';
  const time = form.scheduled_at ? timeOf(form.scheduled_at) : '';

  // ------------------------------------------------------------ mídias

  const mediaList = (target: number): SaveMediaInput[] => (target < 0 ? form.media : form.targets[target]?.media ?? []);
  const setMediaList = (target: number, list: SaveMediaInput[]) => {
    if (target < 0) update({ media: list });
    else updateTarget(target, { media: list });
  };
  const addMedia = (target: number, picked: MediaBrief[]) => {
    setBriefs((b) => ({ ...b, ...Object.fromEntries(picked.map((m) => [m.id, m])) }));
    const ratio = target >= 0 ? ruleOf(form.targets[target].format)?.default_ratio : undefined;
    const current = mediaList(target);
    const fresh = picked.filter((m) => !current.some((c) => c.media_id === m.id)).map((m) => mediaInput(m, ratio));
    setMediaList(target, [...current, ...fresh]);
  };
  const moveMedia = (target: number, index: number, delta: number) => {
    const list = [...mediaList(target)];
    const j = index + delta;
    if (j < 0 || j >= list.length) return;
    [list[index], list[j]] = [list[j], list[index]];
    setMediaList(target, list);
  };
  const patchMedia = (target: number, index: number, patch: Partial<SaveMediaInput>) =>
    setMediaList(
      target,
      mediaList(target).map((m, i) => (i === index ? { ...m, ...patch } : m)),
    );

  // ------------------------------------------------------------ variações

  // A conta vem sempre da escolha de quem cria o post: nunca assume a primeira da rede.
  const addTarget = (format: PostFormat, accountId: number | null) => {
    const r = ruleOf(format);
    const next: SaveTargetInput = {
      format,
      account_id: accountId,
      media: [],
      title: r?.needs_title ? form.title : '',
      link_url: r?.wants_link ? form.link_url ?? '' : '',
      privacy: r?.needs_privacy ? (r.privacy?.[0] ?? '') : '',
    };
    setForm((f) => ({ ...f, targets: [...f.targets, next], strategy: false }));
    setDirty(true);
  };
  const removeTarget = (i: number) => {
    setForm((f) => ({ ...f, targets: f.targets.filter((_, j) => j !== i) }));
    setDirty(true);
    setTab(-1);
  };

  // ------------------------------------------------------------ ações

  const save = async (): Promise<SocialPost | null> => {
    if (!form.title.trim()) {
      setError(new Error('Dê um título interno ao post (só a equipe vê).'));
      return null;
    }
    setBusy('save');
    try {
      const saved = post ? await socialService.updatePost(post.id, form) : await socialService.createPost(form);
      applyPost(saved);
      onChanged(saved.id);
      return saved;
    } catch (err) {
      setError(err);
      return null;
    } finally {
      setBusy(null);
    }
  };

  const runDecision = async () => {
    const kind = decision;
    if (!kind) return;
    let current = post;
    if (dirty || !current) {
      current = await save();
      if (!current) return;
    }
    setBusy(kind);
    try {
      const id = current.id;
      const text = decisionText.trim();
      const next =
        kind === 'submit'
          ? await socialService.submit(id, text)
          : kind === 'approve'
            ? await socialService.approve(id, text)
            : kind === 'request_changes'
              ? await socialService.requestChanges(id, text)
              : kind === 'withdraw'
                ? await socialService.withdraw(id, text)
                : await socialService.markPublished(id, permalinks, text);
      applyPost(next);
      onChanged();
      setDecision(null);
      setDecisionText('');
    } catch (err) {
      setError(err);
    } finally {
      setBusy(null);
    }
  };

  const retryTarget = async (targetId: number) => {
    if (!post) return;
    setBusy(`retry-${targetId}`);
    try {
      applyPost(await socialService.retryTarget(post.id, targetId));
      onChanged();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(null);
    }
  };

  const publishNow = async () => {
    if (!post) return;
    setBusy('publish-now');
    try {
      applyPost(await socialService.publishNow(post.id));
      onChanged();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(null);
    }
  };

  const sendComment = async () => {
    if (!post || !comment.trim()) return;
    setBusy('comment');
    try {
      applyPost(await socialService.comment(post.id, comment.trim()));
      setComment('');
    } catch (err) {
      setError(err);
    } finally {
      setBusy(null);
    }
  };

  const duplicate = async () => {
    if (!post) return;
    setBusy('duplicate');
    try {
      const copy = await socialService.duplicate(post.id);
      onChanged();
      applyPost(copy);
      setTab(-1);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(null);
    }
  };

  const toggleGrid = async () => {
    if (!post) return;
    setBusy('grid');
    try {
      applyPost(await socialService.setGridHidden(post.id, !post.grid_hidden_at));
      onChanged();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(null);
    }
  };

  const remove = async () => {
    if (!post) return;
    setBusy('delete');
    try {
      await socialService.deletePost(post.id);
      onChanged();
      onClose();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(null);
      setConfirmDelete(false);
    }
  };

  const copy = async (key: string, text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied(null), 1500);
    } catch {
      setError(new Error('O navegador não deixou copiar — selecione o texto e copie à mão.'));
    }
  };

  const download = async (mediaId: number) => {
    try {
      const { url } = await mediaService.getUrl(mediaId, 'original', true);
      window.open(url, '_blank', 'noopener');
    } catch (err) {
      setError(err);
    }
  };

  const close = () => {
    if (dirty && post && !window.confirm('Há alterações não salvas. Fechar mesmo assim?')) return;
    onClose();
  };

  // ------------------------------------------------------------ derivados

  const status = post?.status ?? (form.strategy ? 'strategy' : 'draft');
  const issues = post?.issues ?? [];
  const errors = issues.filter((i) => i.level === 'error');
  const activeTarget = tab >= 0 ? form.targets[tab] : undefined;
  const activeRule = activeTarget ? ruleOf(activeTarget.format) : undefined;
  const savedTargetId = (i: number) => form.targets[i]?.id ?? undefined;

  const previewNetwork: Network = activeRule?.network ?? (form.targets[0] ? ruleOf(form.targets[0].format)?.network : undefined) ?? 'instagram';
  const previewMedia = (activeTarget && activeTarget.media.length > 0 ? activeTarget.media : form.media)[0];
  const previewRatio: AspectRatio = (previewMedia?.aspect_ratio as AspectRatio) || activeRule?.default_ratio || '4:5';
  const previewCaption = effectiveCaption(form.caption, activeTarget?.caption);


  if (loading) {
    return (
      <Modal isOpen onClose={onClose} size="full">
        <div className="flex justify-center py-24">
          <Loader2 className="animate-spin text-slate-400" />
        </div>
      </Modal>
    );
  }

  // ------------------------------------------------------------ blocos de UI

  const mediaEditor = (target: number) => {
    const list = mediaList(target);
    const rule = target >= 0 ? activeRule : undefined;
    const max = rule ? rule.max_media : 10;
    return (
      <div className="space-y-2">
        {list.length === 0 && (
          <p className="text-small text-slate-500">
            {target >= 0 ? 'Sem mídias próprias: esta rede usa as mídias do post (aba Base).' : 'Nenhuma mídia ainda.'}
          </p>
        )}
        <div className="flex flex-wrap gap-3">
          {list.map((m, i) => {
            const b = briefs[m.media_id];
            const ratios = rule?.ratios ?? (['original', '4:5', '1:1', '9:16', '2:3'] as AspectRatio[]);
            return (
              <div key={`${m.media_id}-${i}`} className="w-36 rounded-lg border border-slate-200 bg-white p-1.5 space-y-1">
                <button
                  type="button"
                  className="block w-full"
                  title="Clique para escolher o ponto focal do corte"
                  onClick={() => setFocusMedia({ target, index: i })}
                  disabled={readOnly}
                >
                  <BriefThumb
                    media={b}
                    focalX={m.focal_x ?? undefined}
                    focalY={m.focal_y ?? undefined}
                    className={clsx('w-full rounded', ASPECT_CLASS[(m.aspect_ratio as AspectRatio) || 'original'])}
                  />
                </button>
                <p className="truncate text-[11px] text-slate-600" title={b?.title}>
                  {i + 1}. {b?.title ?? `Mídia ${m.media_id}`}
                </p>
                {b?.deleted && <p className="text-[11px] text-red-600">Excluída da biblioteca</p>}
                {b?.kind === 'video' && (
                  <button
                    type="button"
                    className="flex w-full items-center gap-1.5 rounded border border-slate-200 px-1.5 py-1 text-left text-[11px] text-slate-700 hover:bg-slate-50"
                    title="Capa do vídeo: um quadro do vídeo ou uma imagem da biblioteca (Reels do Instagram)"
                    onClick={() => setCoverMedia({ target, index: i })}
                  >
                    {m.cover_media_id && briefs[m.cover_media_id] ? (
                      <BriefThumb media={briefs[m.cover_media_id]} className="h-6 w-4 shrink-0 rounded-sm" />
                    ) : (
                      <Film size={12} className="shrink-0 text-slate-400" />
                    )}
                    <span className="truncate">
                      {m.cover_media_id
                        ? 'Capa: imagem'
                        : m.cover_at_seconds != null
                          ? `Capa: ${formatCoverTime(m.cover_at_seconds)}`
                          : readOnly
                            ? 'Capa: 1º quadro'
                            : 'Escolher capa'}
                    </span>
                  </button>
                )}
                <select
                  className="select-field w-full text-xs py-1"
                  value={m.aspect_ratio ?? ''}
                  disabled={readOnly}
                  onChange={(e) => patchMedia(target, i, { aspect_ratio: (e.target.value || null) as AspectRatio | null })}
                >
                  <option value="">Corte: original</option>
                  {ratios
                    .filter((r) => r !== 'original')
                    .map((r) => (
                      <option key={r} value={r}>
                        Corte {r}
                      </option>
                    ))}
                </select>
                <input
                  className="input-field w-full text-xs py-1"
                  placeholder="Texto alternativo"
                  value={m.alt_text ?? ''}
                  disabled={readOnly}
                  onChange={(e) => patchMedia(target, i, { alt_text: e.target.value })}
                />
                {!readOnly && (
                  <div className="flex items-center justify-between">
                    <div className="flex">
                      <button type="button" className="p-1 text-slate-400 hover:text-slate-700" onClick={() => moveMedia(target, i, -1)} title="Antes">
                        <ArrowUp size={12} className="-rotate-90" />
                      </button>
                      <button type="button" className="p-1 text-slate-400 hover:text-slate-700" onClick={() => moveMedia(target, i, 1)} title="Depois">
                        <ArrowDown size={12} className="-rotate-90" />
                      </button>
                    </div>
                    <button
                      type="button"
                      className="p-1 text-slate-400 hover:text-red-600"
                      title="Tirar do post (continua na biblioteca)"
                      onClick={() => setMediaList(target, list.filter((_, j) => j !== i))}
                    >
                      <X size={12} />
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        {!readOnly && (
          <Button
            size="sm"
            variant="outline"
            icon={<ImagePlus />}
            disabled={list.length >= max}
            onClick={() => setPicker({ target, kinds: rule?.kinds, max: Math.max(0, max - list.length) })}
          >
            Escolher da biblioteca
          </Button>
        )}
      </div>
    );
  };

  const hashtagInserter = (onInsert: (tags: string) => void) =>
    groups.length > 0 && !readOnly ? (
      <select
        className="select-field text-xs py-1 w-48"
        value=""
        onChange={(e) => {
          const g = groups.find((x) => String(x.id) === e.target.value);
          if (g) onInsert(g.hashtags);
        }}
      >
        <option value="">+ Grupo de hashtags…</option>
        {groups.map((g) => (
          <option key={g.id} value={g.id}>
            {g.name} ({g.count})
          </option>
        ))}
      </select>
    ) : null;

  const baseTab = (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div>
          <label className="text-small font-medium text-slate-700">Data</label>
          <DateInputBR value={date} disabled={readOnly} onChange={(v: string) => update({ scheduled_at: joinDateTime(v, time) })} />
        </div>
        <div>
          <label className="text-small font-medium text-slate-700">Hora (Brasília)</label>
          <input
            type="time"
            className="input-field w-full"
            value={time}
            disabled={readOnly || !date}
            onChange={(e) => update({ scheduled_at: joinDateTime(date, e.target.value) })}
          />
        </div>
        <div>
          <label className="text-small font-medium text-slate-700">Pilar</label>
          <select
            className="select-field w-full"
            value={form.pillar_id ?? ''}
            disabled={readOnly}
            onChange={(e) => update({ pillar_id: e.target.value ? Number(e.target.value) : null })}
          >
            <option value="">Sem pilar</option>
            {pillars.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-small font-medium text-slate-700">Responsável</label>
          <select
            className="select-field w-full"
            value={form.owner_member_id ?? ''}
            disabled={readOnly}
            onChange={(e) => update({ owner_member_id: e.target.value ? Number(e.target.value) : null })}
          >
            <option value="">—</option>
            {post?.owner_member_id && !assignableMembers.some((m) => m.id === post.owner_member_id) && (
              <option value={post.owner_member_id}>{post.owner_name}</option>
            )}
            {assignableMembers.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {(status === 'strategy' || form.strategy) && (
        <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-3 text-small text-slate-600 flex gap-2">
          <Info size={16} className="flex-shrink-0 text-slate-400 mt-0.5" />
          <span>
            <strong>Bloco de estratégia</strong>: reserva o lugar na grade com o pilar e a data. Ao escolher redes e mídias e salvar,
            ele vira rascunho.
          </span>
        </div>
      )}

      <div>
        <div className="flex items-center justify-between gap-2">
          <label className="text-small font-medium text-slate-700">Legenda base</label>
          <div className="flex items-center gap-2">
            <Counter n={extractHashtags(form.caption).length} max={0} suffix=" hashtags" />
            <Counter n={charCount(form.caption)} max={2200} />
            {hashtagInserter((tags) => update({ caption: appendHashtags(form.caption ?? '', tags) }))}
          </div>
        </div>
        <textarea
          className="input-field w-full min-h-[140px]"
          value={form.caption ?? ''}
          disabled={readOnly}
          placeholder="Texto que vai para todas as redes (cada rede pode ter a sua na aba dela)."
          onChange={(e) => update({ caption: e.target.value })}
        />
      </div>

      <div>
        <div className="flex items-center justify-between gap-2">
          <label className="text-small font-medium text-slate-700">1º comentário (Instagram)</label>
          <div className="flex items-center gap-2">
            <Counter n={extractHashtags(form.first_comment).length} max={30} suffix=" hashtags" />
            {hashtagInserter((tags) => update({ first_comment: appendHashtags(form.first_comment ?? '', tags) }))}
          </div>
        </div>
        <textarea
          className="input-field w-full min-h-[60px]"
          value={form.first_comment ?? ''}
          disabled={readOnly}
          placeholder="Hashtags no 1º comentário deixam a legenda limpa. No Instagram, legenda + 1º comentário somam até 30 hashtags."
          onChange={(e) => update({ first_comment: e.target.value })}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="text-small font-medium text-slate-700">Link de destino (produto, blog)</label>
          <input
            className="input-field w-full"
            value={form.link_url ?? ''}
            disabled={readOnly}
            placeholder="https://viverorganizer.com.br/..."
            onChange={(e) => update({ link_url: e.target.value })}
          />
          <label className="mt-1 inline-flex items-center gap-1.5 text-xs text-slate-600">
            <input
              type="checkbox"
              className="checkbox-field"
              checked={form.show_in_link_in_bio}
              disabled={readOnly}
              onChange={(e) => update({ show_in_link_in_bio: e.target.checked })}
            />
            Mostrar no Link na bio (página viverorganizer.com.br/links — em construção)
          </label>
        </div>
        <div>
          <label className="text-small font-medium text-slate-700">Notas internas / briefing</label>
          <textarea
            className="input-field w-full min-h-[60px]"
            value={form.notes ?? ''}
            disabled={readOnly}
            onChange={(e) => update({ notes: e.target.value })}
          />
        </div>
      </div>

      <div>
        <h3 className="text-small font-semibold text-slate-700 mb-1">Mídias do post</h3>
        {mediaEditor(-1)}
      </div>

      <div>
        <h3 className="text-small font-semibold text-slate-700 mb-1">Publicar em</h3>
        <p className="text-[11px] text-slate-500 mb-2">
          Clique nas contas em que o post vai sair, em cada formato. Cada combinação de formato e conta vira uma aba com o que é próprio dela (legenda, título, pasta, privacidade, mídias e corte).
        </p>
        <div className="flex flex-wrap gap-2">
          {NETWORKS.map((n) => (
            <div key={n} className="rounded-lg border border-slate-200 bg-white p-2">
              <div className="flex items-center gap-1 text-xs font-semibold text-slate-700 mb-1">
                <NetworkIcon network={n} /> {NETWORK_LABELS[n]}
              </div>
              <div className="flex flex-col gap-1">
                {formatsOf(rules, n).map((r) => {
                  const netAccounts = accounts.filter((a) => a.network === n);
                  const pendingIdx = form.targets.findIndex((t) => t.format === r.format && !t.account_id);
                  return (
                    <div key={r.format}>
                      <p className="text-[11px] text-slate-500">{r.label.replace(/^[^—]+— /, '')}</p>
                      <div className="flex flex-wrap gap-1">
                        {netAccounts.map((a) => {
                          const idx = findTargetIndex(form.targets, r.format, a.id);
                          return (
                            <button
                              key={a.id}
                              type="button"
                              aria-pressed={idx >= 0}
                              disabled={readOnly}
                              onClick={() => (idx >= 0 ? removeTarget(idx) : addTarget(r.format, a.id))}
                              className={clsx(
                                'text-left text-[11px] rounded px-1.5 py-0.5 border [overflow-wrap:anywhere]',
                                idx >= 0 ? 'bg-emerald-50 border-emerald-300 text-emerald-700' : 'border-slate-200 hover:bg-slate-100 text-slate-600',
                              )}
                            >
                              {idx >= 0 ? <Check size={10} className="inline mr-0.5" /> : <Plus size={10} className="inline mr-0.5" />}@{a.username}
                            </button>
                          );
                        })}
                        {pendingIdx >= 0 ? (
                          <button
                            type="button"
                            onClick={() => setTab(pendingIdx)}
                            className="text-left text-[11px] rounded px-1.5 py-0.5 border border-amber-300 bg-amber-50 text-amber-700"
                          >
                            <AlertTriangle size={10} className="inline mr-0.5" />
                            Sem conta
                          </button>
                        ) : (
                          netAccounts.length === 0 && (
                            <button
                              type="button"
                              disabled={readOnly}
                              onClick={() => addTarget(r.format, null)}
                              className="text-left text-[11px] rounded px-1.5 py-0.5 hover:bg-slate-100 text-slate-500"
                            >
                              <Plus size={10} className="inline mr-0.5" />
                              Planejar sem conta
                            </button>
                          )
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const targetTab = (i: number) => {
    const t = form.targets[i];
    const r = ruleOf(t.format);
    if (!r) return null;
    const opts = readOptions(t.options_json);
    const setOpt = (k: string, v: boolean | string) => updateTarget(i, { options_json: JSON.stringify({ ...opts, [k]: v }) });
    const caption = effectiveCaption(form.caption, t.caption);
    const tagCount = extractHashtags(r.first_comment ? `${caption} ${form.first_comment ?? ''}` : caption).length;
    // Conta já usada em outra variação do mesmo formato não entra: seria o mesmo post duas vezes.
    const netAccounts = accounts.filter(
      (a) => a.network === r.network && !form.targets.some((o, j) => j !== i && o.format === t.format && o.account_id === a.id),
    );
    const tid = savedTargetId(i);
    const own = issues.filter((x) => tid !== undefined && x.target_id === tid);
    return (
      <div className="space-y-4">
        <div className="rounded-lg bg-slate-50 border border-slate-200 p-2 text-[11px] text-slate-600">
          {r.label}: {r.min_media === r.max_media ? `${r.max_media} mídia` : `${r.min_media}–${r.max_media} mídias`} (
          {r.kinds.map((k) => (k === 'image' ? 'foto' : 'vídeo')).join(' ou ')}) · proporção {r.ratios.join(', ')}
          {r.max_caption > 0 && ` · legenda até ${r.max_caption.toLocaleString('pt-BR')}`}
          {r.max_hashtags > 0 && ` · até ${r.max_hashtags} hashtags`}
          {r.max_seconds ? ` · vídeo até ${r.max_seconds >= 60 ? `${r.max_seconds / 60} min` : `${r.max_seconds} s`}` : ''}
        </div>
        {own.length > 0 && (
          <ul className="space-y-1">
            {own.map((x, k) => (
              <li key={k} className={clsx('text-xs flex gap-1', x.level === 'error' ? 'text-red-700' : 'text-amber-700')}>
                <AlertTriangle size={12} className="mt-0.5 flex-shrink-0" /> {x.message.replace(`${r.label}: `, '')}
              </li>
            ))}
          </ul>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="text-small font-medium text-slate-700">Conta</label>
            <select
              className="select-field w-full"
              value={t.account_id ?? ''}
              disabled={readOnly}
              onChange={(e) => updateTarget(i, { account_id: e.target.value ? Number(e.target.value) : null })}
            >
              <option value="">{netAccounts.length ? 'Escolha a conta' : 'Nenhuma conta cadastrada'}</option>
              {netAccounts.map((a) => (
                <option key={a.id} value={a.id}>
                  @{a.username} {a.publish_mode === 'reminder' ? '(lembrete)' : ''}
                </option>
              ))}
            </select>
          </div>
          {r.needs_privacy && (
            <div>
              <label className="text-small font-medium text-slate-700">Privacidade</label>
              <select className="select-field w-full" value={t.privacy ?? ''} disabled={readOnly} onChange={(e) => updateTarget(i, { privacy: e.target.value })}>
                <option value="">Escolha…</option>
                {(r.privacy ?? []).map((p) => (
                  <option key={p} value={p}>
                    {PRIVACY_LABELS[p] ?? p}
                  </option>
                ))}
              </select>
            </div>
          )}
          {r.needs_title && (
            <div>
              <div className="flex justify-between">
                <label className="text-small font-medium text-slate-700">Título</label>
                <Counter n={charCount(t.title)} max={r.max_title ?? 100} />
              </div>
              <input className="input-field w-full" value={t.title ?? ''} disabled={readOnly} onChange={(e) => updateTarget(i, { title: e.target.value })} />
            </div>
          )}
          {r.needs_board && (
            <div>
              <label className="text-small font-medium text-slate-700">Pasta do Pinterest</label>
              <input
                className="input-field w-full"
                value={t.board_name ?? ''}
                disabled={readOnly}
                placeholder="Closets planejados"
                onChange={(e) => updateTarget(i, { board_name: e.target.value })}
              />
            </div>
          )}
          {r.wants_link && (
            <div className="md:col-span-2">
              <label className="text-small font-medium text-slate-700">Link de destino do pin</label>
              <input
                className="input-field w-full"
                value={t.link_url ?? ''}
                disabled={readOnly}
                placeholder={form.link_url || 'https://viverorganizer.com.br/...'}
                onChange={(e) => updateTarget(i, { link_url: e.target.value })}
              />
            </div>
          )}
        </div>

        {r.collaborators && (() => {
          const raw = typeof opts.collaborators === 'string' ? opts.collaborators : '';
          const { names, invalid } = parseCollaborators(raw);
          return (
            <div>
              <div className="flex items-center justify-between gap-2">
                <label className="text-small font-medium text-slate-700 inline-flex items-center gap-1">
                  <Users size={14} /> Collab com
                </label>
                <Counter n={names.length} max={MAX_COLLABORATORS} suffix=" perfis" />
              </div>
              <input
                className="input-field w-full"
                value={raw}
                disabled={readOnly}
                placeholder="@perfil (até 3, separados por vírgula)"
                onChange={(e) => setOpt('collaborators', e.target.value)}
              />
              <p className="mt-1 text-[11px] text-slate-500">
                O perfil recebe um convite no Instagram e aparece como coautor depois de aceitar. Só perfis públicos.
              </p>
              {(names.length > 0 || invalid.length > 0) && (
                <div className="mt-1 flex flex-wrap gap-1.5">
                  {names.map((n) => (
                    <a
                      key={n}
                      href={`https://www.instagram.com/${n}/`}
                      target="_blank"
                      rel="noreferrer"
                      title="Abrir o perfil para conferir"
                      className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] text-emerald-800 hover:underline [overflow-wrap:anywhere]"
                    >
                      @{n} <ExternalLink size={10} />
                    </a>
                  ))}
                  {invalid.map((n) => (
                    <span key={n} className="rounded-full bg-red-50 px-2 py-0.5 text-[11px] text-red-700 [overflow-wrap:anywhere]">
                      {n} não é um @ válido
                    </span>
                  ))}
                </div>
              )}
            </div>
          );
        })()}

        {t.format === 'tiktok' && (
          <div className="flex flex-wrap gap-4 text-sm text-slate-700">
            {[
              ['allow_comment', 'Permitir comentários'],
              ['allow_duet', 'Permitir dueto'],
              ['allow_stitch', 'Permitir stitch'],
              ['branded_content', 'Conteúdo de marca (parceria paga)'],
            ].map(([k, label]) => (
              <label key={k} className="inline-flex items-center gap-1.5">
                <input
                  type="checkbox"
                  className="checkbox-field"
                  checked={opts[k] === undefined ? k.startsWith('allow') : !!opts[k]}
                  disabled={readOnly}
                  onChange={(e) => setOpt(k, e.target.checked)}
                />
                {label}
              </label>
            ))}
          </div>
        )}
        {t.format === 'yt_short' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            <label className="inline-flex items-center gap-1.5 text-slate-700">
              <input
                type="checkbox"
                className="checkbox-field"
                checked={!!opts.made_for_kids}
                disabled={readOnly}
                onChange={(e) => setOpt('made_for_kids', e.target.checked)}
              />
              Feito para crianças
            </label>
            <input
              className="input-field w-full"
              placeholder="Tags do YouTube (separadas por vírgula)"
              value={typeof opts.tags === 'string' ? opts.tags : ''}
              disabled={readOnly}
              onChange={(e) => setOpt('tags', e.target.value)}
            />
          </div>
        )}

        {r.max_caption > 0 && (
          <div>
            <div className="flex items-center justify-between gap-2">
              <label className="text-small font-medium text-slate-700">
                {r.network === 'pinterest' ? 'Descrição do pin' : r.network === 'youtube' ? 'Descrição' : 'Legenda própria desta rede'}
              </label>
              <div className="flex items-center gap-2">
                <Counter n={tagCount} max={r.max_hashtags} suffix=" hashtags" />
                <Counter n={charCount(caption)} max={r.max_caption} />
                {!readOnly && (
                  <button type="button" className="text-[11px] text-emerald-700 hover:underline" onClick={() => updateTarget(i, { caption: form.caption ?? '' })}>
                    Copiar da base
                  </button>
                )}
              </div>
            </div>
            <textarea
              className="input-field w-full min-h-[120px]"
              value={t.caption ?? ''}
              disabled={readOnly}
              placeholder="Vazio = usa a legenda base"
              onChange={(e) => updateTarget(i, { caption: e.target.value })}
            />
          </div>
        )}

        <div>
          <h3 className="text-small font-semibold text-slate-700 mb-1">Mídias desta rede</h3>
          {mediaEditor(i)}
        </div>

        {!readOnly && (
          <Button size="sm" variant="outline" icon={<Trash2 />} onClick={() => removeTarget(i)}>
            Tirar esta rede do post
          </Button>
        )}
      </div>
    );
  };

  const focus = focusMedia ? mediaList(focusMedia.target)[focusMedia.index] : undefined;
  const cover = coverMedia ? mediaList(coverMedia.target)[coverMedia.index] : undefined;
  const coverAllowsImage = coverMedia
    ? coverImageAllowed(
        rules,
        form.targets.map((t) => t.format),
        coverMedia.target >= 0 ? form.targets[coverMedia.target]?.format ?? null : null,
      )
    : false;
  const focusBrief = focus ? briefs[focus.media_id] : undefined;

  // ------------------------------------------------------------ render

  return (
    <>
      <Modal isOpen onClose={close} size="full" closeOnBackdrop={false} showCloseButton={false}>
        <div className="flex flex-col max-h-[94vh]">
          {/* Cabeçalho */}
          <div className="px-5 py-3 border-b border-slate-200 flex flex-wrap items-center gap-3">
            <input
              className="input-field basis-full sm:basis-auto sm:flex-1 min-w-0 sm:min-w-[240px] text-base font-semibold order-last sm:order-none"
              placeholder="Título interno do post (só a equipe vê)"
              value={form.title}
              disabled={readOnly}
              onChange={(e) => update({ title: e.target.value })}
            />
            <StatusChip status={status} />
            {post?.scheduled_at && <span className="text-xs text-slate-500">{formatPostTime(post.scheduled_at)}</span>}
            {post?.activity_id && (
              <button type="button" className="text-xs text-emerald-700 hover:underline inline-flex items-center gap-1" onClick={() => openActivity(post.activity_id!)}>
                <ExternalLink size={12} /> Atividade
              </button>
            )}
            <button type="button" onClick={close} className="p-1 text-slate-400 hover:text-slate-600" aria-label="Fechar">
              <X size={20} />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto lg:overflow-hidden flex flex-col lg:flex-row">
            {/* Formulário */}
            <div className="flex-1 lg:overflow-y-auto custom-scrollbar p-3 sm:p-5">
              <div className="flex flex-wrap gap-1 border-b border-slate-200 mb-4">
                <button
                  type="button"
                  onClick={() => setTab(-1)}
                  className={clsx('px-3 py-1.5 text-sm rounded-t-lg', tab === -1 ? 'bg-white border border-b-white border-slate-200 -mb-px font-semibold' : 'text-slate-500')}
                >
                  Base
                </button>
                {form.targets.map((t, i) => {
                  const r = ruleOf(t.format);
                  const tid = savedTargetId(i);
                  const bad = issues.some((x) => x.level === 'error' && tid !== undefined && x.target_id === tid);
                  return (
                    <button
                      key={`${t.format}-${i}`}
                      type="button"
                      onClick={() => setTab(i)}
                      className={clsx(
                        'px-3 py-1.5 text-sm rounded-t-lg inline-flex items-center gap-1',
                        tab === i ? 'bg-white border border-b-white border-slate-200 -mb-px font-semibold' : 'text-slate-500',
                      )}
                    >
                      {r && <NetworkIcon network={r.network} size={12} />}
                      {r?.label.replace(/^[^—]+— /, '') ?? t.format}
                      <span className="text-[11px] font-normal text-slate-500">
                        {accounts.find((a) => a.id === t.account_id)?.username ? `@${accounts.find((a) => a.id === t.account_id)?.username}` : 'sem conta'}
                      </span>
                      {bad && <AlertTriangle size={11} className="text-red-500" />}
                    </button>
                  );
                })}
              </div>
              {tab === -1 ? baseTab : targetTab(tab)}
            </div>

            {/* Lateral: prévia, problemas, conversa */}
            <aside className="lg:w-[380px] flex-shrink-0 border-t lg:border-t-0 lg:border-l border-slate-200 lg:overflow-y-auto custom-scrollbar p-4 space-y-4 bg-slate-50">
              <section>
                <h3 className="text-xs font-semibold uppercase text-slate-500 mb-2 flex items-center gap-1">
                  Prévia <NetworkIcon network={previewNetwork} size={12} />
                </h3>
                <div className="mx-auto max-w-[260px] rounded-2xl border-4 border-slate-800 bg-white overflow-hidden shadow">
                  <div className="flex items-center gap-1.5 px-2 py-1.5 text-[11px] font-semibold">
                    <span className="w-5 h-5 rounded-full bg-gradient-to-tr from-amber-400 to-pink-600" />
                    {accounts.find((a) => a.id === (activeTarget?.account_id ?? form.targets[0]?.account_id))?.username ?? 'viverorganizer'}
                  </div>
                  <BriefThumb
                    media={previewMedia ? briefs[previewMedia.media_id] : undefined}
                    focalX={previewMedia?.focal_x ?? undefined}
                    focalY={previewMedia?.focal_y ?? undefined}
                    className={clsx('w-full', ASPECT_CLASS[previewRatio])}
                  />
                  <p className="px-2 py-1.5 text-[11px] text-slate-800 whitespace-pre-wrap line-clamp-6">{previewCaption || <em className="text-slate-400">sem legenda</em>}</p>
                  {previewNetwork === 'instagram' && form.first_comment && (
                    <p className="px-2 pb-2 text-[10px] text-slate-500 line-clamp-2">
                      <strong>1º comentário:</strong> {form.first_comment}
                    </p>
                  )}
                </div>
              </section>

              {issues.length > 0 && (
                <section>
                  <h3 className="text-xs font-semibold uppercase text-slate-500 mb-1">Antes de enviar ({issues.length})</h3>
                  <ul className="space-y-1">
                    {issues.map((x, k) => (
                      <li key={k} className={clsx('text-xs flex gap-1', x.level === 'error' ? 'text-red-700' : 'text-amber-700')}>
                        <AlertTriangle size={12} className="mt-0.5 flex-shrink-0" />
                        {x.message}
                      </li>
                    ))}
                  </ul>
                  {dirty && <p className="text-[11px] text-slate-400 mt-1">Salve para conferir de novo.</p>}
                </section>
              )}

              {post && post.targets.length > 0 && ['approved', 'scheduled', 'publishing', 'failed', 'partially_published', 'published'].includes(post.status) && (
                <section className="space-y-2">
                  <h3 className="text-xs font-semibold uppercase text-slate-500">Publicação</h3>
                  <p className="text-[11px] text-slate-500">
                    <strong>Automático</strong>: o sistema publica na hora marcada. <strong>Lembrete</strong>: baixe a mídia, copie a legenda,
                    publique pelo app e marque como publicado.
                  </p>
                  {post.targets.map((t) => {
                    const cap = effectiveCaption(post.caption, t.caption);
                    const key = `cap-${t.id}`;
                    const manual = t.mode === 'reminder' && t.status !== 'published';
                    return (
                      <div key={t.id} className="rounded border border-slate-200 bg-white p-2 space-y-1">
                        <div className="text-xs font-medium flex flex-wrap items-center gap-1">
                          <NetworkIcon network={t.network} size={12} /> {ruleOf(t.format)?.label ?? t.format}
                          <span className={clsx('rounded px-1 text-[10px]', t.mode === 'auto' ? 'bg-indigo-50 text-indigo-700' : 'bg-slate-100 text-slate-600')}>
                            {t.mode === 'auto' ? 'automático' : 'lembrete'}
                          </span>
                          <span
                            className={clsx(
                              'rounded px-1 text-[10px]',
                              t.status === 'published' && 'bg-emerald-100 text-emerald-800',
                              t.status === 'failed' && 'bg-red-100 text-red-700',
                              t.status === 'publishing' && 'bg-amber-100 text-amber-800',
                              t.status === 'pending' && 'bg-slate-100 text-slate-600',
                            )}
                          >
                            {TARGET_STATUS_LABELS[t.status]}
                          </span>
                          {t.permalink && (
                            <a href={t.permalink} target="_blank" rel="noreferrer" className="ml-auto text-emerald-700 hover:underline">
                              ver post
                            </a>
                          )}
                        </div>
                        {t.error_message && (
                          <p className={clsx('text-[11px] [overflow-wrap:anywhere]', t.status === 'published' ? 'text-amber-700' : 'text-red-700')}>
                            {t.error_message}
                            {t.status === 'pending' && t.attempts > 0 && ` (nova tentativa automática — ${t.attempts} até agora)`}
                          </p>
                        )}
                        <div className="flex flex-wrap gap-1">
                          {t.status === 'failed' && t.mode === 'auto' && canWrite && (
                            <Button size="sm" variant="outline" icon={<RotateCcw />} loading={busy === `retry-${t.id}`} onClick={() => retryTarget(t.id)}>
                              Tentar de novo
                            </Button>
                          )}
                          {manual && (
                            <>
                              <Button size="sm" variant="outline" icon={<ClipboardCopy />} onClick={() => copy(key, cap)}>
                                {copied === key ? 'Copiada!' : 'Legenda'}
                              </Button>
                              {t.network === 'instagram' && post.first_comment && (
                                <Button size="sm" variant="outline" icon={<Hash />} onClick={() => copy(`fc-${t.id}`, post.first_comment ?? '')}>
                                  {copied === `fc-${t.id}` ? 'Copiado!' : '1º comentário'}
                                </Button>
                              )}
                              {(post.media.some((m) => m.target_id === t.id) ? post.media.filter((m) => m.target_id === t.id) : post.media.filter((m) => !m.target_id)).map(
                                (m, k) => (
                                  <Button key={`${m.media_id}-${k}`} size="sm" variant="outline" icon={<Download />} onClick={() => download(m.media_id)}>
                                    Mídia {k + 1}
                                  </Button>
                                ),
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </section>
              )}

              {post && (
                <section>
                  <h3 className="text-xs font-semibold uppercase text-slate-500 mb-2 flex items-center gap-1">
                    <MessageSquare size={12} /> Conversa e decisões
                  </h3>
                  <ul className="space-y-2">
                    {(post.comments ?? []).map((c) => (
                      <li key={c.id} className={clsx('rounded-lg p-2 text-xs', c.kind === 'comment' ? 'bg-white border border-slate-200' : 'bg-slate-100')}>
                        <div className="text-slate-500">
                          <strong className="text-slate-700">{c.author_name ?? c.author_email}</strong> {COMMENT_KIND_LABELS[c.kind] ?? c.kind} ·{' '}
                          {formatDateTimeBR(c.created_at)}
                        </div>
                        {c.body && <p className="mt-0.5 whitespace-pre-wrap text-slate-800">{c.body}</p>}
                      </li>
                    ))}
                    {(post.comments ?? []).length === 0 && <li className="text-xs text-slate-400">Sem comentários.</li>}
                  </ul>
                  <div className="mt-2 flex gap-1">
                    <textarea
                      className="input-field flex-1 text-xs min-h-[44px]"
                      placeholder="Comentar (avisa o responsável no sino)"
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                    />
                    <Button size="sm" icon={<Send />} loading={busy === 'comment'} disabled={!comment.trim()} onClick={sendComment}>
                      {''}
                    </Button>
                  </div>
                </section>
              )}
            </aside>
          </div>

          {/* Rodapé de ações */}
          <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 flex flex-wrap items-center gap-2">
            {post && canWrite && post.can_edit && (
              <Button size="sm" variant="outline" icon={<Trash2 />} onClick={() => setConfirmDelete(true)}>
                Excluir
              </Button>
            )}
            {post && canWrite && (post.status === 'published' || post.status === 'partially_published') && (
              <Button size="sm" variant="outline" icon={<LayoutGrid />} loading={busy === 'grid'} onClick={toggleGrid}>
                {post.grid_hidden_at ? 'Voltar para a grade' : 'Retirar da grade'}
              </Button>
            )}
            {post && canWrite && (
              <Button size="sm" variant="outline" icon={<Copy />} loading={busy === 'duplicate'} onClick={duplicate}>
                Duplicar
              </Button>
            )}
            <span className="flex-1" />
            {post?.approved_by && (
              <span className="text-xs text-emerald-700 inline-flex items-center gap-1">
                <BadgeCheck size={14} /> Aprovado por {post.approved_by} em {formatDateTimeBR(post.approved_at)}
              </span>
            )}
            {/* Cancelar/Fechar explícito no rodapé (o X do título passava despercebido). */}
            <Button size="sm" variant="outline" icon={<X />} disabled={!!busy} onClick={close}>
              {!post || dirty ? 'Cancelar' : 'Fechar'}
            </Button>
            {!readOnly && (
              <Button size="sm" variant="outline" icon={<Save />} loading={busy === 'save'} disabled={!dirty} onClick={() => save()}>
                Salvar
              </Button>
            )}
            {canWrite && (!post || ['draft', 'changes_requested', 'strategy'].includes(post.status)) && (
              <Button size="sm" icon={<Send />} disabled={!!busy || (!dirty && errors.length > 0)} onClick={() => setDecision('submit')}>
                Enviar para aprovação
              </Button>
            )}
            {post && canWrite && ['awaiting_approval', 'changes_requested'].includes(post.status) && (
              <Button size="sm" variant="outline" icon={<Undo2 />} onClick={() => setDecision('withdraw')}>
                Voltar para rascunho
              </Button>
            )}
            {post?.can_approve && ['awaiting_approval', 'approved', 'scheduled'].includes(post.status) && (
              <Button size="sm" variant="outline" icon={<RotateCcw />} onClick={() => setDecision('request_changes')}>
                Pedir ajustes
              </Button>
            )}
            {post?.can_approve && post.status === 'awaiting_approval' && (
              <Button size="sm" variant="success" icon={<BadgeCheck />} onClick={() => setDecision('approve')}>
                Aprovar
              </Button>
            )}
            {post &&
              canPublishNow &&
              !dirty &&
              ['approved', 'scheduled', 'failed', 'partially_published'].includes(post.status) &&
              post.targets.some((t) => t.mode === 'auto' && t.status === 'pending') && (
                <Button size="sm" variant="secondary" icon={<Send />} loading={busy === 'publish-now'} onClick={publishNow}>
                  Publicar agora
                </Button>
              )}
            {post &&
              canWrite &&
              ['approved', 'scheduled', 'failed', 'partially_published'].includes(post.status) &&
              post.targets.some((t) => t.mode === 'reminder' && t.status !== 'published') && (
                <Button size="sm" variant="success" icon={<Check />} onClick={() => setDecision('publish')}>
                  Marcar como publicado
                </Button>
              )}
          </div>
        </div>
      </Modal>

      {/* Decisão do fluxo */}
      <Modal isOpen={decision !== null} onClose={() => setDecision(null)} size="md">
        <div className="p-5 space-y-3">
          <h2 className="text-h3">
            {decision === 'submit' && 'Enviar para aprovação'}
            {decision === 'approve' && 'Aprovar o post'}
            {decision === 'request_changes' && 'Pedir ajustes'}
            {decision === 'withdraw' && 'Voltar para rascunho'}
            {decision === 'publish' && 'Marcar como publicado'}
          </h2>
          <p className="text-small text-slate-600">
            {decision === 'submit' && 'O gestor recebe o aviso no sino. Enquanto estiver em aprovação, alterar o conteúdo continua possível.'}
            {decision === 'approve' && (post?.scheduled_at ? `O post fica agendado para ${formatPostTime(post.scheduled_at)} e ganha o selo verde.` : 'O post fica aprovado (sem data) e ganha o selo verde.')}
            {decision === 'request_changes' && 'Diga o que mudar — o responsável recebe o aviso e o post volta para ele.'}
            {decision === 'withdraw' && 'O post sai da fila de aprovação e volta a ser rascunho.'}
            {decision === 'publish' &&
              'Use depois de publicar pelo app da rede (modo lembrete). A atividade é concluída e o uso das mídias fica registrado na biblioteca.'}
          </p>
          {decision === 'publish' &&
            post?.targets.map((t) => (
              <div key={t.id}>
                <label className="text-xs text-slate-600 inline-flex items-center gap-1">
                  <NetworkIcon network={t.network} size={12} /> Link do post publicado ({ruleOf(t.format)?.label ?? t.format}) — opcional
                </label>
                <input
                  className="input-field w-full text-sm"
                  placeholder="https://..."
                  value={permalinks[t.id] ?? t.permalink ?? ''}
                  onChange={(e) => setPermalinks((p) => ({ ...p, [t.id]: e.target.value }))}
                />
              </div>
            ))}
          <textarea
            className="input-field w-full min-h-[80px]"
            placeholder={decision === 'request_changes' ? 'O que precisa ser ajustado (obrigatório)' : 'Comentário (opcional)'}
            value={decisionText}
            onChange={(e) => setDecisionText(e.target.value)}
          />
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setDecision(null)}>
              Cancelar
            </Button>
            <Button
              variant={decision === 'approve' || decision === 'publish' ? 'success' : 'primary'}
              loading={!!busy}
              disabled={decision === 'request_changes' && !decisionText.trim()}
              onClick={runDecision}
            >
              Confirmar
            </Button>
          </div>
        </div>
      </Modal>

      {/* Ponto focal */}
      <Modal isOpen={!!focus} onClose={() => setFocusMedia(null)} size="lg">
        {focus && focusMedia && (
          <div className="p-5 space-y-3">
            <h2 className="text-h3">Ponto focal do corte</h2>
            <p className="text-small text-slate-600">
              Clique no ponto da foto que não pode ser cortado (rosto, produto). O corte de cada rede fica centrado nele.
            </p>
            <div
              className="relative mx-auto max-w-md cursor-crosshair select-none"
              onClick={(e) => {
                const rect = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
                const x = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
                const y = Math.min(1, Math.max(0, (e.clientY - rect.top) / rect.height));
                patchMedia(focusMedia.target, focusMedia.index, { focal_x: Math.round(x * 1000) / 1000, focal_y: Math.round(y * 1000) / 1000 });
              }}
            >
              {focusBrief?.preview_url || focusBrief?.thumb_url ? (
                <img src={focusBrief.preview_url || focusBrief.thumb_url} alt={focusBrief.title} className="w-full rounded" />
              ) : (
                <div className="h-64 bg-slate-100 rounded" />
              )}
              {focus.focal_x != null && focus.focal_y != null && (
                <span
                  className="absolute w-5 h-5 -ml-2.5 -mt-2.5 rounded-full border-2 border-white bg-emerald-500/80 shadow"
                  style={{ left: `${focus.focal_x * 100}%`, top: `${focus.focal_y * 100}%` }}
                />
              )}
            </div>
            <div className="flex justify-between">
              <Button variant="outline" size="sm" onClick={() => patchMedia(focusMedia.target, focusMedia.index, { focal_x: null, focal_y: null })}>
                Centralizar
              </Button>
              <Button size="sm" onClick={() => setFocusMedia(null)}>
                Pronto
              </Button>
            </div>
          </div>
        )}
      </Modal>

      <ReelCoverDialog
        isOpen={!!cover}
        onClose={() => setCoverMedia(null)}
        video={cover ? briefs[cover.media_id] : undefined}
        value={{ cover_at_seconds: cover?.cover_at_seconds ?? null, cover_media_id: cover?.cover_media_id ?? null }}
        coverBrief={cover?.cover_media_id ? briefs[cover.cover_media_id] : undefined}
        allowImage={coverAllowsImage}
        readOnly={readOnly}
        onSave={(v, picked) => {
          if (!coverMedia) return;
          if (picked) setBriefs((bs) => ({ ...bs, [picked.id]: picked }));
          patchMedia(coverMedia.target, coverMedia.index, v);
        }}
      />

      {picker && (
        <MediaPickerDialog
          isOpen
          onClose={() => setPicker(null)}
          kinds={picker.kinds}
          max={picker.max}
          onPick={(picked) => addMedia(picker.target, picked)}
        />
      )}

      <ConfirmDialog
        isOpen={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={remove}
        title="Excluir post"
        message="O post e a atividade dele somem do planejamento. As mídias continuam na biblioteca."
        confirmText="Excluir"
        severity="danger"
        loading={busy === 'delete'}
      />
      <ApiErrorModal error={error} onClose={() => setError(null)} />
    </>
  );
};

export default PostComposer;
